import React, { FC, useMemo } from 'react';
import { AthleteAttendanceTeam } from '../../models/attendance.models';
import { PeriodBreakdownItem } from '../../models/stats.models';
import { PeriodTeamAttendanceTile } from '../team-attendance-breakdown/team-attendance-breakdown.component';
import TeamAttendancePager from '../team-attendance-breakdown/team-attendance-pager.component';
import { hasAttendanceData } from '../team-attendance-breakdown/team-attendance-breakdown.helpers';
import { getMeasurementUnit, ZONE_CONFIGS } from '../../helpers/general.helpers';
import { buildBarSegments, buildSportStats, buildZoneStats } from './period-summary.helpers';

export interface PeriodDetailBreakdownProps {
  period: PeriodBreakdownItem;
  attendanceTeams?: AthleteAttendanceTeam[] | null;
  attendanceLoading?: boolean;
}

const PeriodDetailBreakdown: FC<PeriodDetailBreakdownProps> = ({ period, attendanceTeams, attendanceLoading }) => {
  const distanceUnit = getMeasurementUnit();

  const zoneBarSegments = useMemo(
    () =>
      buildBarSegments(
        period.totalDurationBreakdown ?? [],
        ZONE_CONFIGS.map((z) => z.barColor)
      ),
    [period.totalDurationBreakdown]
  );
  const zoneStats = useMemo(
    () => buildZoneStats(period.totalDurationBreakdown, period.totalDistanceBreakdown),
    [period.totalDurationBreakdown, period.totalDistanceBreakdown]
  );
  const sportStats = useMemo(() => buildSportStats(period.sport), [period.sport]);
  const sportBarSegments = useMemo(
    () =>
      buildBarSegments(
        sportStats.map((s) => s.durationSeconds),
        sportStats.map((s) => s.color)
      ),
    [sportStats]
  );

  const showPeriodAttendance = !attendanceLoading && hasAttendanceData(attendanceTeams);
  const periodAttendanceTeams = attendanceTeams ?? [];

  return (
    <>
      <div className="pd-columns">
        <div className="pd-col">
          <div className="pd-section-label">Training Zones</div>
          <div className="pd-zone-grid">
            {zoneStats.map((zone) => (
              <div
                key={zone.label}
                className="pd-zone-stat"
                style={{ background: zone.config.bg, borderLeftColor: zone.config.borderColor }}
              >
                <div>
                  <div className="ztag" style={{ color: zone.config.textColor }}>
                    {zone.label}
                  </div>
                  <div className="zval">{zone.durationFormatted}</div>
                  <div className="zlbl-dist">
                    {zone.distance > 0 ? `${zone.distance.toFixed(1)} ${distanceUnit}` : `0.0 ${distanceUnit}`}
                  </div>
                  <div className="zlbl">{zone.percent.toFixed(1)}%</div>
                </div>
              </div>
            ))}
          </div>
          <div className="pd-bar" style={{ marginTop: 8 }}>
            {zoneBarSegments.map((segment, index) => (
              <span key={index} style={{ background: segment.color, flex: segment.flex }} />
            ))}
          </div>
        </div>

        <div className="pd-col">
          <div className="pd-section-label">Duration &amp; Distance by Sport</div>
          <div className="pd-sport-grid">
            {sportStats.map((sport) => (
              <div key={sport.key} className="pd-sport-item">
                <div className="sport-dot" style={{ background: sport.color }} />
                <div>
                  <div className="sport-hrs">
                    {sport.durationFormatted} · {sport.distance.toFixed(1)} {distanceUnit}
                  </div>
                  <div className="sport-name">
                    {sport.label} · {sport.percent.toFixed(1)}%
                  </div>
                </div>
              </div>
            ))}
          </div>
          {sportBarSegments.length > 0 && (
            <div className="pd-sport-bar" style={{ marginTop: 8 }}>
              {sportBarSegments.map((segment, index) => (
                <span key={index} style={{ background: segment.color, flex: segment.flex }} />
              ))}
            </div>
          )}
        </div>
      </div>

      {showPeriodAttendance && (
        <div className="pd-col-full">
          <TeamAttendancePager
            items={periodAttendanceTeams}
            gridClassName="pd-attendance-grid"
            title="Team Attendance"
            titleClassName="pd-section-label"
            getItemKey={(team) => team.teamId}
            renderItem={(team, idx) => (
              <PeriodTeamAttendanceTile key={team.teamId} team={team} periodIndex={period.index} themeIndex={idx} />
            )}
          />
        </div>
      )}
    </>
  );
};

export default PeriodDetailBreakdown;
